import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STRIKE_VECTORS = [
    { id: "offensive_security", label: "Offensive Security", hint: "Pentest, red team, exploit dev" },
    { id: "cloud", label: "Cloud", hint: "AWS / Azure / GCP hardening" },
    { id: "backend", label: "Backend", hint: "API, services, data plane" },
    { id: "ot_ics", label: "OT / ICS", hint: "Industrial, SCADA, energy" },
    { id: "compliance", label: "Compliance", hint: "SOC 2, HIPAA, PCI, CMMC" },
    { id: "incident_response", label: "Incident Response", hint: "DFIR, forensics, recovery" },
];

const CLEARANCE_LEVELS = ["Privateer", "Field Operative", "Contractor"];

export default function MissionIntake() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { search } = useLocation();
    const [existing, setExisting] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [skillsText, setSkillsText] = useState("");
    const [strikeVectors, setStrikeVectors] = useState([]);
    const [yearsHandsOn, setYearsHandsOn] = useState("");
    const [holdsClearance, setHoldsClearance] = useState(false);
    const [clearanceLevel, setClearanceLevel] = useState("Privateer");
    const [ndaAck, setNdaAck] = useState(false);

    useEffect(() => {
        let active = true;
        api.get("/foundry/mission-intake/me")
            .then((r) => active && setExisting(r.data))
            .catch(() => active && setExisting(null))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, []);

    if (!user || typeof user !== "object") {
        return (
            <div className="mx-auto max-w-md px-6 py-24 text-center">
                <div className="caption text-[var(--brand-accent)]">Mission Intake</div>
                <h1 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA]">Sign in to begin</h1>
                <p className="mt-3 text-[#A3A3A3]">Authentication required for the Intake.</p>
                <button
                    onClick={() => navigate({ pathname: "/login", search: `?next=/foundry/mission-intake` })}
                    className="mt-8 rounded-sm px-6 py-3 text-sm font-medium"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    data-testid="intake-signin"
                >
                    Sign in
                </button>
            </div>
        );
    }

    if (!user.is_privateer) {
        return (
            <div className="mx-auto max-w-xl px-6 py-24 text-center">
                <div className="caption text-[var(--brand-accent)]">Mission Intake · Locked</div>
                <h1 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA]">Boarding required first</h1>
                <p className="mt-4 text-[#A3A3A3]">
                    Complete the Director Interview and the $250 First Light boarding fee before running Mission Intake.
                    The Trinity Boarding must be honored in order.
                </p>
                <button
                    onClick={() => navigate({ pathname: "/foundry/interview", search })}
                    className="mt-8 rounded-sm px-6 py-3 text-sm font-medium"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    data-testid="intake-go-boarding"
                >
                    Begin Boarding
                </button>
            </div>
        );
    }

    if (loading) {
        return <div className="mx-auto max-w-md px-6 py-24 text-center text-[#A3A3A3]">Loading intake…</div>;
    }

    if (existing && existing.completed) {
        return (
            <div data-testid="intake-already-cleared" className="mx-auto max-w-xl px-6 py-24 text-center">
                <div className="caption text-[var(--brand-accent)]">Mission Intake · Cleared</div>
                <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA]">
                    Trinity complete
                </h1>
                <p className="mt-4 text-[#A3A3A3]">
                    Clearance: <span className="text-[var(--brand-accent)]">{existing.clearance || "Privateer"}</span> ·
                    Skills: {(existing.mission_skills || []).length}
                </p>
                <p className="mt-2 text-xs text-[#737373]">
                    Cleared at {new Date(existing.mission_intake_completed_at).toLocaleString()}
                </p>
                <button
                    onClick={() => navigate({ pathname: "/exchange", search })}
                    className="mt-8 rounded-sm px-6 py-3 text-sm font-medium"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    data-testid="intake-view-dossiers"
                >
                    View Unlocked Dossiers
                </button>
            </div>
        );
    }

    const skillsArr = skillsText.split(",").map((s) => s.trim()).filter(Boolean);
    const canSubmit = skillsArr.length >= 3 && strikeVectors.length >= 1 && yearsHandsOn !== "" && ndaAck;

    function toggleVector(id) {
        setStrikeVectors((curr) =>
            curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]
        );
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (!canSubmit) {
            toast.error("Provide 3+ skills, at least one strike vector, years of hands-on, and acknowledge the NDA.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/foundry/mission-intake", {
                skills: skillsArr,
                strike_vectors: strikeVectors,
                years_hands_on: Number(yearsHandsOn),
                holds_clearance: holdsClearance,
                clearance_level: clearanceLevel,
                nda_acknowledged: ndaAck,
            });
            toast.success(`Cleared at ${data.clearance}. Dossiers unlocked.`);
            navigate({ pathname: "/exchange", search });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div data-testid="mission-intake-page" className="mx-auto max-w-3xl px-6 py-16">
            <div className="caption text-[var(--brand-accent)]">Trinity Boarding · Step 3 of 3</div>
            <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA]">Mission Intake</h1>
            <p className="mt-3 text-sm text-[#A3A3A3]">
                Declare your strike vectors and acknowledge the NDA. This unlocks risk scores, vulnerability lists,
                and real target names on the Opportunity Exchange.
            </p>

            <form onSubmit={onSubmit} className="mt-10 space-y-8">
                <Field label="Technical skills (comma-separated · min 3)">
                    <input
                        data-testid="intake-skills"
                        value={skillsText}
                        onChange={(e) => setSkillsText(e.target.value)}
                        placeholder="AppSec, AWS, Active Directory, Burp, SIEM"
                        className="w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
                    />
                    <div className="mt-1.5 text-xs text-[#737373]">
                        {skillsArr.length} skill{skillsArr.length === 1 ? "" : "s"} parsed
                    </div>
                </Field>

                <div>
                    <div className="caption text-[#737373] mb-3">Strike vectors (pick 1 or more)</div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        {STRIKE_VECTORS.map((v) => {
                            const on = strikeVectors.includes(v.id);
                            return (
                                <button
                                    type="button"
                                    key={v.id}
                                    onClick={() => toggleVector(v.id)}
                                    data-testid={`intake-vector-${v.id}`}
                                    data-state={on ? "active" : "inactive"}
                                    className={`rounded-sm border px-4 py-3 text-left transition-all ${
                                        on
                                            ? "border-[var(--brand-accent)] bg-[rgba(201,169,110,0.06)]"
                                            : "border-white/10 bg-white/[0.02] hover:border-white/25"
                                    }`}
                                >
                                    <div className="font-medium text-[#FAFAFA]">{v.label}</div>
                                    <div className="text-xs text-[#737373]">{v.hint}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <Field label="Years of hands-on operation">
                    <input
                        data-testid="intake-years"
                        type="number"
                        min={0}
                        max={60}
                        value={yearsHandsOn}
                        onChange={(e) => setYearsHandsOn(e.target.value)}
                        className="w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
                    />
                </Field>

                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Clearance level">
                        <select
                            data-testid="intake-clearance"
                            value={clearanceLevel}
                            onChange={(e) => setClearanceLevel(e.target.value)}
                            className="w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
                        >
                            {CLEARANCE_LEVELS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <label className="flex cursor-pointer items-center gap-3 rounded-sm border border-white/10 bg-white/[0.02] px-4 py-3">
                        <input
                            type="checkbox"
                            data-testid="intake-holds-clearance"
                            checked={holdsClearance}
                            onChange={(e) => setHoldsClearance(e.target.checked)}
                            className="h-4 w-4 accent-[var(--brand-accent)]"
                        />
                        <span className="text-sm text-[#FAFAFA]">I hold a government / industry clearance</span>
                    </label>
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-sm border border-white/10 bg-white/[0.02] px-5 py-4">
                    <input
                        type="checkbox"
                        data-testid="intake-nda"
                        checked={ndaAck}
                        onChange={(e) => setNdaAck(e.target.checked)}
                        className="mt-1 h-4 w-4 accent-[var(--brand-accent)]"
                    />
                    <div>
                        <div className="font-medium text-[#FAFAFA]">NDA Acknowledgement</div>
                        <div className="mt-1 text-xs text-[#A3A3A3]">
                            I will not share target intelligence (risk scores, vulnerabilities, dossier contents) with any party outside the active engagement.
                            Breach voids my clearance and may trigger civil action.
                        </div>
                    </div>
                </label>

                <button
                    type="submit"
                    data-testid="btn-intake-submit"
                    disabled={!canSubmit || submitting}
                    className="w-full rounded-sm py-3.5 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)] disabled:opacity-50"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                >
                    {submitting ? "Clearing…" : "Sign intake & unlock dossiers"}
                </button>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="caption text-[#737373]">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
